import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe } from "lucide-react";
import { axiosInstance } from "../App";
import { toast } from "sonner";

export default function LanguageSwitcher({ variant = "default" }) {
  const { i18n, t } = useTranslation();

  const changeLanguage = async (lng) => {
    try {
      i18n.changeLanguage(lng);
      localStorage.setItem('language', lng);
      
      // Save to backend if user is logged in
      const token = localStorage.getItem('token');
      if (token) {
        try {
          await axiosInstance.put("/auth/language", null, {
            params: { language: lng }
          });
        } catch (err) {
          console.error("Error saving language to backend:", err);
        }
      }
      
      toast.success(t('language.changed'));
    } catch (error) {
      console.error("Error changing language:", error);
    }
  };

  const languages = [
    { code: "de", label: t('language.de'), flag: "🇩🇪" },
    { code: "en", label: t('language.en'), flag: "🇬🇧" },
    { code: "cs", label: t('language.cs'), flag: "🇨🇿" },
    { code: "ro", label: t('language.ro'), flag: "🇷🇴" },
  ];

  const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];

  if (variant === "card") {
    // For login card - darker style with better visibility
    return (
      <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-3 rounded-lg">
        <Globe className="w-5 h-5 text-blue-600" />
        <Select value={i18n.language} onValueChange={changeLanguage}>
          <SelectTrigger className="w-[200px] bg-white border-gray-300 hover:border-blue-500 transition-colors">
            <SelectValue>
              <span className="flex items-center gap-2">
                <span className="text-xl">{currentLanguage.flag}</span>
                <span className="font-medium text-gray-700">{currentLanguage.label}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {languages.map((lang) => (
              <SelectItem key={lang.code} value={lang.code}>
                <span className="flex items-center gap-2">
                  <span className="text-xl">{lang.flag}</span>
                  <span className="font-medium">{lang.label}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (variant === "header") {
    // For dashboard header - white style
    return (
      <div className="flex items-center gap-2">
        <Globe className="w-4 h-4 text-white" />
        <Select value={i18n.language} onValueChange={changeLanguage}>
          <SelectTrigger className="w-[180px] bg-white/10 border-white/20 text-white hover:bg-white/20">
            <SelectValue>
              <span className="flex items-center gap-2">
                <span>{currentLanguage.flag}</span>
                <span>{currentLanguage.label}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {languages.map((lang) => (
              <SelectItem key={lang.code} value={lang.code}>
                <span className="flex items-center gap-2">
                  <span>{lang.flag}</span>
                  <span>{lang.label}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // Default variant for login
  return (
    <div className="flex items-center gap-2">
      <Globe className="w-4 h-4 text-white" />
      <Select value={i18n.language} onValueChange={changeLanguage}>
        <SelectTrigger className="w-[180px] bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm">
          <SelectValue>
            <span className="flex items-center gap-2">
              <span>{currentLanguage.flag}</span>
              <span>{currentLanguage.label}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {languages.map((lang) => (
            <SelectItem key={lang.code} value={lang.code}>
              <span className="flex items-center gap-2">
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
